// Employee Management Configuration
// Types and interfaces for the Employee Management module

// Core Types
export type TeamMemberType = 'Engineer' | 'Draftsperson' | 'Accounts';
export type ManagerialRole = 'manager' | 'basic' | 'qa';
export type EmploymentStatus = 'active' | 'inactive' | 'on_leave' | 'terminated';

// Employee interface from database
export interface Employee {
  id: string;
  first_name: string;
  last_name: string;
  type: TeamMemberType;
  role: ManagerialRole;
  email: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  current_charge_rate: string | null;
  rate_effective_date: string | null;
  // Signing identity, stamped onto documents when a signature slot is signed.
  signature_storage_path: string | null;
  // Placement of the signature image over the document's signature block:
  // scale multiplies the base render height (two text lines), the offsets move
  // it right/down in mm from the bottom-left of the signature space.
  signature_scale: number;
  signature_offset_x_mm: number;
  signature_offset_y_mm: number;
  position_title: string | null;
  registration_number: string | null;
  is_cpeng: boolean;
  // Length of the standard working day; drives the timesheet targets and the
  // length of a day when leave hours are shown in days. Default 7.5.
  standard_hours_per_day: number;
  // When their day usually starts, "HH:mm:ss"; the Day view's first entry.
  default_start_time: string;
  // The lunch break, not worked: timed entries lose what falls inside it.
  lunch_start_time: string;
  lunch_minutes: number;
}

// Charge rate interface
export interface ChargeRate {
  id: number;
  employee_id: string;
  charge_rate: string;
  effective_date: string;
  end_date: string | null;
}

// Timesheet summary for employee
export interface EmployeeTimesheetSummary {
  employee_id: string;
  total_hours: number;
  total_entries: number;
  last_submission: string | null;
  current_week_hours: number;
}

// Filter configuration
export interface FilterConfig {
  employeeType: TeamMemberType | null;
  role: ManagerialRole | null;
  isActive: boolean | null;
  hasChargeRate: boolean | null;
  searchText: string;
}

// Form data for adding/editing employees
export interface EmployeeFormData {
  first_name: string;
  last_name: string;
  type: TeamMemberType;
  role: ManagerialRole;
  email: string;
  // Printed under the signature on documents this person signs. Blank is fine —
  // the PDF simply omits the line.
  position_title: string;
  registration_number: string;
  is_cpeng: boolean;
  // Signature placement, adjusted by dragging/scaling the preview. Saved with
  // the form like the rest of the signing identity.
  signature_scale: number;
  signature_offset_x_mm: number;
  signature_offset_y_mm: number;
  // Kept as text while editing so the field can be cleared; parsed on save.
  standard_hours_per_day: string;
  // "HH:mm" from a time input.
  default_start_time: string;
  lunch_start_time: string;
  // Text while editing; 0 switches the deduction off.
  lunch_minutes: string;
}

// Form data for charge rates
export interface ChargeRateFormData {
  charge_rate: string;
  effective_date: string;
  end_date: string;
}

// ---------------------------------------------------------------------------
// Leave
// ---------------------------------------------------------------------------
// Everything is in HOURS. Days are hours / standard_hours_per_day and exist
// only on screen. The shapes below mirror get_employee_leave (13_leave).

/** The two leave types that carry a balance. 'personal' IS annual leave. */
export type BalanceLeaveType = 'personal' | 'sick_leave';

export const BALANCE_LEAVE_LABELS: Record<BalanceLeaveType, string> = {
  personal: 'Annual leave',
  sick_leave: 'Sick leave'
};

export interface LeaveTotals {
  opening: number;
  accrued: number;
  adjustments: number;
  taken: number;
  /** Leave in a week that has not been submitted. Listed, not deducted. */
  pending: number;
  balance: number;
}

/** One completed pay period, or the part of one a set of terms covers. */
export interface LeaveAccrualRow {
  period_start: string;
  period_end: string;
  segment_start: string;
  segment_end: string;
  days: number;
  /** The calendar year the row falls in. A period across 1 January is two rows. */
  leave_year: number;
  contracted_hours_per_week: number;
  annual_leave_weeks_per_year: number;
  sick_leave_days_per_year: number;
  annual_hours: number;
  sick_hours: number;
}

export interface LeaveEntry {
  id: number;
  date: string;
  hours: number;
  leave_type: BalanceLeaveType;
  description: string | null;
  /** False while the week is still a draft: not yet taken off the balance. */
  counted: boolean;
  week_ending_date: string;
  week_status: string;
}

export interface LeaveAdjustment {
  id: number;
  leave_type: BalanceLeaveType;
  hours: number;
  effective_date: string;
  note: string;
  created_at: string;
  created_by_name: string | null;
}

export interface LeaveEntitlement {
  id: number;
  /** null is the baseline: the terms that apply from the start. */
  effective_from: string | null;
  contracted_hours_per_week: number;
  annual_leave_weeks_per_year: number;
  sick_leave_days_per_year: number;
  accrues: boolean;
  /** Set while the employee is inactive, on rows dated ahead. Earns nothing. */
  paused: boolean;
  note: string | null;
}

/**
 * One calendar year of one leave type.
 *
 * Annual leave carries over, so its years are a running balance:
 * `brought_forward` is the year before's `remaining`. Sick leave does not. Its
 * balance goes back to zero on 1 January, `brought_forward` is always 0, and a
 * closed year's `remaining` is what was left to pay out.
 */
export interface LeaveYear extends Omit<LeaveTotals, 'balance'> {
  year: number;
  is_current: boolean;
  brought_forward: number;
  /** At the end of the year, or now for the year in progress. */
  remaining: number;
}

export interface EmployeeLeave {
  employee_id: string;
  as_of: string;
  is_active: boolean;
  standard_hours_per_day: number;
  /** null: no timesheet week and no start date set, so nothing accrues. */
  accrual_start: string | null;
  accrual_start_override: string | null;
  first_timesheet_week: string | null;
  next_period_end: string;
  has_baseline: boolean;
  /** The stored settings, as stored. The form is filled from this. */
  profile: {
    accrual_start_date: string | null;
    opening_annual_hours: number;
    opening_sick_hours: number;
  };
  current_year: number;
  /** Newest first, from the year leave started to this one. */
  sick_years: LeaveYear[];
  annual_years: LeaveYear[];
  /** personal runs from the start to now; sick_leave is this calendar year only. */
  totals: Record<BalanceLeaveType, LeaveTotals>;
  accruals: LeaveAccrualRow[];
  entries: LeaveEntry[];
  adjustments: LeaveAdjustment[];
  entitlements: LeaveEntitlement[];
}

/** The defaults the database applies when no baseline has been saved. */
export const DEFAULT_LEAVE_TERMS = {
  contracted_hours_per_week: 37.5,
  annual_leave_weeks_per_year: 4,
  sick_leave_days_per_year: 10
};

// The leave settings saved by the modal's Save button. Text while editing so
// a field can be cleared; parsed on save. The entitlement itself is not here:
// each entitlement is a row of its own and saves when it is added.
export interface LeaveSettingsDraft {
  /** "" leaves the start at the first timesheet week. */
  accrual_start_date: string;
  opening_annual_hours: string;
  opening_sick_hours: string;
}

export interface LeaveAdjustmentInput {
  leave_type: BalanceLeaveType;
  hours: number;
  effective_date: string;
  note: string;
}

export interface LeaveEntitlementInput {
  /** null is the entitlement that applies from the start. */
  effective_from: string | null;
  contracted_hours_per_week: number;
  annual_leave_weeks_per_year: number;
  sick_leave_days_per_year: number;
  accrues: boolean;
  note: string;
}

// Form validation errors
export interface FormErrors {
  first_name?: string;
  last_name?: string;
  type?: string;
  role?: string;
  email?: string;
  is_active?: string;
  standard_hours_per_day?: string;
  default_start_time?: string;
  lunch?: string;
  charge_rate?: string;
  effective_date?: string;
  end_date?: string;
  opening_annual_hours?: string;
  opening_sick_hours?: string;
  general?: string;
}

// Modal state management
export interface ModalState {
  editEmployee: boolean;
  manageChargeRates: boolean;
  selectedEmployee: Employee | null;
}

// Employee type options for dropdowns
export const EMPLOYEE_TYPE_OPTIONS = [
  { id: 'engineer', value: 'Engineer', label: 'Engineer' },
  { id: 'draftsperson', value: 'Draftsperson', label: 'Draftsperson' },
  { id: 'accounts', value: 'Accounts', label: 'Accounts' }
];

// Role options for dropdowns
export const ROLE_OPTIONS = [
  { id: 'basic', value: 'basic', label: 'Basic User' },
  { id: 'manager', value: 'manager', label: 'Manager' },
  { id: 'qa', value: 'qa', label: 'QA (Auditor)' }
];

// Active status options for filtering
export const ACTIVE_STATUS_OPTIONS = [
  { id: 'all', value: 'all', label: 'All Employees' },
  { id: 'active', value: 'active', label: 'Active Only' },
  { id: 'inactive', value: 'inactive', label: 'Inactive Only' }
];

// Utility functions
export const formatCurrency = (amount: string | number | null): string => {
  if (!amount) return 'Not set';
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  return `$${numAmount.toFixed(2)}`;
};

export const formatDate = (dateString: string | null): string => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString();
};

export const getEmployeeName = (employee: Employee): string => {
  return `${employee.first_name} ${employee.last_name}`;
};

export const getEmployeeStatus = (employee: Employee): string => {
  return employee.is_active ? 'Active' : 'Inactive';
};

export const getChargeRateStatus = (employee: Employee): string => {
  if (!employee.current_charge_rate) return 'No rate set';
  return `${formatCurrency(employee.current_charge_rate)} (${formatDate(employee.rate_effective_date)})`;
};

// Validation functions
export const validateEmail = (email: string): boolean => {
  if (!email) return true; // Email is optional
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validateChargeRate = (rate: string): boolean => {
  const numRate = parseFloat(rate);
  return !isNaN(numRate) && numRate > 0;
};

export const validateRequired = (value: string): boolean => {
  return value.trim().length > 0;
};

// Sort functions
export const sortEmployees = (employees: Employee[], sortBy: keyof Employee, ascending: boolean = true): Employee[] => {
  return [...employees].sort((a, b) => {
    let aVal = a[sortBy];
    let bVal = b[sortBy];

    // Handle null values
    if (aVal === null && bVal === null) return 0;
    if (aVal === null) return ascending ? 1 : -1;
    if (bVal === null) return ascending ? -1 : 1;

    // Handle string comparison
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return ascending ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }

    // Handle numeric comparison
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return ascending ? aVal - bVal : bVal - aVal;
    }

    return 0;
  });
};

// Filter functions
export const filterEmployees = (employees: Employee[], filters: FilterConfig): Employee[] => {
  return employees.filter(employee => {
    // Filter by employee type
    if (filters.employeeType && employee.type !== filters.employeeType) {
      return false;
    }

    // Filter by role
    if (filters.role && employee.role !== filters.role) {
      return false;
    }

    // Filter by active status
    if (filters.isActive !== null && employee.is_active !== filters.isActive) {
      return false;
    }

    // Filter by charge rate presence
    if (filters.hasChargeRate !== null) {
      const hasRate = employee.current_charge_rate !== null;
      if (hasRate !== filters.hasChargeRate) {
        return false;
      }
    }

    // Filter by search text
    if (filters.searchText) {
      const searchLower = filters.searchText.toLowerCase();
      const fullName = `${employee.first_name} ${employee.last_name}`.toLowerCase();
      const email = (employee.email || '').toLowerCase();
      
      if (!fullName.includes(searchLower) && !email.includes(searchLower)) {
        return false;
      }
    }

    return true;
  });
}; 